import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, api } from "@/App";
import { toast } from "sonner";
import Sidebar from "@/components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { 
  Briefcase,
  Clock,
  MapPin,
  Wrench,
  DollarSign,
  Plus,
  Trash2,
  CheckCircle2,
  FileText
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";

export default function TechnicianView() {
  const { unitId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [estimateDialogOpen, setEstimateDialogOpen] = useState(false);
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [estimate, setEstimate] = useState({
    diagnosis: "",
    repair_plan: "",
    parts: [],
    labor_cost: 0,
    notes: ""
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchMyTickets();
  }, [unitId, user?.id]);

  // Live-drop cancelled tickets without needing a manual refresh
  useEffect(() => {
    const onCancelled = (e) => {
      const id = e?.detail?.ticket_id;
      if (!id) { fetchMyTickets(); return; }
      setTickets(prev => prev.filter(t => t.id !== id));
    };
    window.addEventListener("efacilicare:ticket_cancelled", onCancelled);
    window.addEventListener("efacilicare:ticket_deleted", onCancelled);
    return () => {
      window.removeEventListener("efacilicare:ticket_cancelled", onCancelled);
      window.removeEventListener("efacilicare:ticket_deleted", onCancelled);
    };
  }, []);

  const fetchMyTickets = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/tickets?unit_id=${unitId}&assignee_id=${user?.id}`);
      setTickets(res.data.tickets);
    } catch (error) {
      toast.error("Failed to load tickets");
    } finally {
      setLoading(false);
    }
  };

  const openEstimateDialog = (ticket) => {
    setSelectedTicket(ticket);
    if (ticket.cost_estimate) {
      setEstimate({
        diagnosis: ticket.cost_estimate.diagnosis || "",
        repair_plan: ticket.cost_estimate.repair_plan || "",
        parts: ticket.cost_estimate.parts || [],
        labor_cost: ticket.cost_estimate.labor_cost || 0,
        notes: ticket.cost_estimate.notes || ""
      });
    } else {
      setEstimate({
        diagnosis: "",
        repair_plan: "",
        parts: [],
        labor_cost: 0,
        notes: ""
      });
    }
    setEstimateDialogOpen(true);
  };

  const addPart = () => {
    setEstimate(prev => ({
      ...prev,
      parts: [...prev.parts, { name: "", quantity: 1, unit_cost: 0 }]
    }));
  };

  const updatePart = (index, field, value) => {
    setEstimate(prev => {
      const newParts = [...prev.parts];
      newParts[index] = { ...newParts[index], [field]: value };
      return { ...prev, parts: newParts };
    });
  };

  const removePart = (index) => {
    setEstimate(prev => ({
      ...prev,
      parts: prev.parts.filter((_, i) => i !== index)
    }));
  };

  const calculateTotal = () => {
    const partsTotal = estimate.parts.reduce((sum, p) => sum + (p.quantity * p.unit_cost), 0);
    return partsTotal + (parseFloat(estimate.labor_cost) || 0);
  };

  const handleSubmitEstimate = async () => {
    if (!estimate.diagnosis || !estimate.repair_plan) {
      toast.error("Please fill in diagnosis and repair plan");
      return;
    }

    setSubmitting(true);
    try {
      await api.post(`/tickets/${selectedTicket.id}/cost-estimate`, {
        diagnosis: estimate.diagnosis,
        repair_plan: estimate.repair_plan,
        parts: estimate.parts,
        labor_cost: parseFloat(estimate.labor_cost) || 0,
        notes: estimate.notes
      });
      toast.success("Cost estimate submitted for approval");
      setEstimateDialogOpen(false);
      fetchMyTickets();
    } catch (error) {
      toast.error("Failed to submit estimate");
    } finally {
      setSubmitting(false);
    }
  };

  const handleComplete = async () => {
    setSubmitting(true);
    try {
      await api.post(`/tickets/${selectedTicket.id}/complete`);
      toast.success("Ticket marked as resolved");
      setCompleteDialogOpen(false);
      fetchMyTickets();
    } catch (error) {
      toast.error("Failed to complete ticket");
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      "Assigned to Technician": "bg-yellow-100 text-yellow-700",
      "Vendor Assigned": "bg-yellow-100 text-yellow-700",
      "Pending Cost Approval": "bg-orange-100 text-orange-700",
      "Rejected - Needs Review": "bg-red-100 text-red-700",
      "Repair Authorized": "bg-green-100 text-green-700",
      "Resolved": "bg-gray-100 text-gray-700",
    };
    return styles[status] || "bg-gray-100 text-gray-700";
  };

  const canSubmitEstimate = (ticket) => {
    return ["Assigned to Technician", "Vendor Assigned", "Rejected - Needs Review"].includes(ticket.status);
  };

  const canComplete = (ticket) => {
    return ticket.status === "Repair Authorized";
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar unitId={unitId} />
      
      <main className="flex-1 p-4 md:p-8 overflow-auto">
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
              <Briefcase className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">My Assignments</h1>
              <p className="text-gray-600">Tickets assigned to you</p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto"></div>
          </div>
        ) : tickets.length > 0 ? (
          <div className="space-y-4">
            {tickets.map((ticket) => (
              <Card key={ticket.id} className="bg-white border-gray-200">
                <CardContent className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <button
                          onClick={() => navigate(`/ticket/${ticket.id}`)}
                          className="text-lg font-semibold text-indigo-600 hover:underline"
                        >
                          {ticket.job_number}
                        </button>
                        <Badge className={getStatusBadge(ticket.status)}>
                          {ticket.status}
                        </Badge>
                        {ticket.priority && (
                          <Badge className={
                            ticket.priority === 'P1' ? 'bg-red-100 text-red-700' :
                            ticket.priority === 'P2' ? 'bg-orange-100 text-orange-700' :
                            ticket.priority === 'P3' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-gray-100 text-gray-700'
                          }>
                            {ticket.priority}
                          </Badge>
                        )}
                      </div>

                      <p className="text-gray-700 mb-4">{ticket.description}</p>

                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                        <div className="flex items-center gap-2 text-gray-600">
                          <MapPin className="w-4 h-4" />
                          <span>{ticket.restaurant_name}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <Wrench className="w-4 h-4" />
                          <span>{ticket.equipment_category}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <Clock className="w-4 h-4" />
                          <span>{formatDistanceToNow(new Date(ticket.created_at), { addSuffix: true })}</span>
                        </div>
                      </div>

                      {ticket.cost_estimate && (
                        <div className="mt-4 p-3 bg-indigo-50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <DollarSign className="w-4 h-4 text-indigo-600" />
                            <span className="font-semibold text-indigo-700">
                              Estimate: ${ticket.cost_estimate.total_cost?.toFixed(2)}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col gap-2">
                      {canSubmitEstimate(ticket) && (
                        <Button
                          onClick={() => openEstimateDialog(ticket)}
                          className="bg-indigo-600 hover:bg-indigo-700"
                          data-testid={`estimate-btn-${ticket.id}`}
                        >
                          <DollarSign className="w-4 h-4 mr-2" />
                          {ticket.cost_estimate ? "Update Estimate" : "Submit Estimate"}
                        </Button>
                      )}
                      {canComplete(ticket) && (
                        <Button
                          onClick={() => {
                            setSelectedTicket(ticket);
                            setCompleteDialogOpen(true);
                          }}
                          className="bg-green-600 hover:bg-green-700"
                          data-testid={`complete-btn-${ticket.id}`}
                        >
                          <CheckCircle2 className="w-4 h-4 mr-2" />
                          Mark Complete
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        onClick={() => navigate(`/ticket/${ticket.id}`)}
                      >
                        <FileText className="w-4 h-4 mr-2" />
                        View Details
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="bg-white border-gray-200">
            <CardContent className="py-12 text-center">
              <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Assignments</h3>
              <p className="text-gray-500">You don't have any tickets assigned to you</p>
            </CardContent>
          </Card>
        )}

        {/* Cost Estimate Dialog */}
        <Dialog open={estimateDialogOpen} onOpenChange={setEstimateDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Cost Estimate - {selectedTicket?.job_number}</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Diagnosis *</Label>
                <Textarea
                  placeholder="What is the root cause of the issue?"
                  value={estimate.diagnosis}
                  onChange={(e) => setEstimate(prev => ({ ...prev, diagnosis: e.target.value }))}
                  rows={3}
                  data-testid="diagnosis-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Repair Plan *</Label>
                <Textarea
                  placeholder="How will you fix the issue?"
                  value={estimate.repair_plan}
                  onChange={(e) => setEstimate(prev => ({ ...prev, repair_plan: e.target.value }))}
                  rows={3}
                  data-testid="repair-plan-input"
                />
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Parts</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addPart}
                    data-testid="add-part-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Part
                  </Button>
                </div>

                {estimate.parts.map((part, index) => (
                  <div key={part._cid || `${part.name || "part"}-${index}`} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <Input
                      placeholder="Part name"
                      value={part.name}
                      onChange={(e) => updatePart(index, "name", e.target.value)}
                      className="flex-1"
                      data-testid={`part-name-${index}`}
                    />
                    <Input
                      type="number"
                      placeholder="Qty"
                      value={part.quantity}
                      onChange={(e) => updatePart(index, "quantity", parseInt(e.target.value) || 0)}
                      className="w-20"
                      data-testid={`part-qty-${index}`}
                    />
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="Unit Cost"
                        value={part.unit_cost}
                        onChange={(e) => updatePart(index, "unit_cost", parseFloat(e.target.value) || 0)}
                        className="w-28 pl-7"
                        data-testid={`part-cost-${index}`}
                      />
                    </div>
                    <span className="text-sm font-medium text-gray-700 w-20 text-right">
                      ${(part.quantity * part.unit_cost).toFixed(2)}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removePart(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>

              <div className="space-y-2">
                <Label>Labor Cost ($)</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    value={estimate.labor_cost}
                    onChange={(e) => setEstimate(prev => ({ ...prev, labor_cost: e.target.value }))}
                    className="pl-7"
                    data-testid="labor-cost-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Additional Notes</Label>
                <Textarea
                  placeholder="Any additional information..."
                  value={estimate.notes}
                  onChange={(e) => setEstimate(prev => ({ ...prev, notes: e.target.value }))}
                  rows={2}
                  data-testid="estimate-notes-input"
                />
              </div>

              <div className="p-4 bg-indigo-50 rounded-lg">
                <div className="flex justify-between items-center text-lg font-semibold">
                  <span className="text-gray-700">Total Estimate</span>
                  <span className="text-indigo-700">${calculateTotal().toFixed(2)}</span>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setEstimateDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmitEstimate}
                disabled={submitting}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="submit-estimate-btn"
              >
                {submitting ? "Submitting..." : "Submit for Approval"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Complete Dialog */}
        <Dialog open={completeDialogOpen} onOpenChange={setCompleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Complete Ticket</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-gray-600">
                Are you sure you want to mark ticket <span className="font-semibold">{selectedTicket?.job_number}</span> as resolved?
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCompleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleComplete}
                disabled={submitting}
                className="bg-green-600 hover:bg-green-700"
                data-testid="confirm-complete-btn"
              >
                {submitting ? "Completing..." : "Mark as Resolved"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
